import { useState } from "react";
import { motion } from "framer-motion";
import { CircleNotch } from "@phosphor-icons/react";
import { merchantMeta } from "@/lib/merchants";

export function MerchantLogo({ name = "", size = 44, className = "" }) {
  const meta = merchantMeta(name);
  const [failed, setFailed] = useState(!meta.domain);
  const px = size;
  if (failed) {
    return (
      <div
        data-testid="merchant-logo"
        style={{ width: px, height: px, background: meta.color, fontSize: px * 0.36 }}
        className={`rounded-xl flex items-center justify-center text-white font-head font-bold shrink-0 ${className}`}
      >
        {meta.initials}
      </div>
    );
  }
  return (
    <div
      data-testid="merchant-logo"
      style={{ width: px, height: px }}
      className={`rounded-xl bg-white border border-riq-border flex items-center justify-center overflow-hidden shrink-0 ${className}`}
    >
      <img
        src={`https://logo.clearbit.com/${meta.domain}`}
        onError={() => setFailed(true)}
        alt={name}
        className="w-3/4 h-3/4 object-contain"
      />
    </div>
  );
}

export const CATEGORY_COLORS = {
  Groceries: "#FF6B6B",
  Restaurants: "#FFAA00",
  Fuel: "#42C97C",
  Household: "#5A67D8",
  Clothing: "#F472B6",
  Electronics: "#805AD5",
  Subscriptions: "#6E3FFC",
  Insurance: "#0EA5E9",
  Telecommunications: "#6366F1",
  "Medical Aid": "#10B981",
  Loans: "#F59E0B",
  "Home Loan": "#D97706",
  "Car Installment": "#EF4444",
  Rates: "#8B5CF6",
  "Garden Services": "#65A30D",
  "Pool Services": "#06B6D4",
  "Domestic Helper": "#EC4899",
  "Health & Beauty": "#FB7185",
  Entertainment: "#38BDF8",
  Transport: "#14B8A6",
  Other: "#A0AEC0",
};

export const catColor = (c) => CATEGORY_COLORS[c] || "#A0AEC0";

export function Card({ className = "", children, ...props }) {
  const hasBg = /(?:^|\s)bg-/.test(className);
  return (
    <div
      className={`rounded-2xl border border-riq-border ${hasBg ? "" : "bg-white"} p-6 shadow-sm ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function SectionTitle({ children, action, className = "" }) {
  return (
    <div className={`flex items-end justify-between mb-4 ${className}`}>
      <h2 className="font-head text-xl sm:text-2xl font-semibold tracking-tight text-riq-primary">{children}</h2>
      {action}
    </div>
  );
}

export function Label({ children }) {
  return <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{children}</div>;
}

export function Pill({ children, color = "#6E3FFC", active, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="rounded-full px-4 py-2 text-sm font-medium transition-transform duration-200 hover:-translate-y-0.5"
      style={{
        background: active ? color : "#EFF1F8",
        color: active ? "#fff" : "#5B6376",
      }}
    >
      {children}
    </button>
  );
}

export function Spinner({ size = 24, className = "" }) {
  return <CircleNotch size={size} weight="bold" className={`animate-spin ${className}`} />;
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
      <Spinner size={32} className="text-riq-primary" />
      <div className="text-sm">{label}</div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center text-center py-16 px-6"
    >
      <div className="w-16 h-16 rounded-2xl bg-riq-sage/60 flex items-center justify-center mb-5">
        {Icon && <Icon size={30} weight="duotone" className="text-riq-primary" />}
      </div>
      <h3 className="font-head text-xl font-semibold text-riq-primary mb-2">{title}</h3>
      {subtitle && <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">{subtitle}</p>}
      {action && <div className="mt-6">{action}</div>}
    </motion.div>
  );
}

export const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: "easeOut" },
};

export const stagger = {
  animate: { transition: { staggerChildren: 0.06 } },
};
